import React from "react";

import { ChevronRight, Home, LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

export type PageHeaderBreadcrumb = {
  title: string;
  /** When set, renders as a navigable link (non-current crumbs). */
  to?: string;
};

interface PageHeaderProps {
  title: string | React.ReactNode;
  icon?: LucideIcon;
  hideIcon?: boolean;
  breadcrumb?: PageHeaderBreadcrumb[];
  actions?: React.ReactNode[];
  centerContent?: React.ReactNode;
  className?: string;
  hideBreadcrumb?: boolean;
  subTitle?: string | React.ReactNode;
  /** More room between the title, sub-title and breadcrumb rows. */
  roomy?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  icon: IconComponent = Home,
  hideIcon = false,
  breadcrumb = [],
  actions = [],
  centerContent,
  className,
  hideBreadcrumb = false,
  subTitle = null,
  roomy = false,
}) => {
  const shouldShowBreadcrumb = !hideBreadcrumb && breadcrumb.length > 0;

  return (
    <div
      className={`z-[1] border-b border-gray-200 bg-white px-4 ${roomy ? "py-4" : "py-3"} ${className ? className : ""}`}
    >
      <div className="flex min-h-10 items-center justify-between gap-3 md:gap-4">
        <div className={`flex min-w-0 flex-col ${roomy ? "gap-3" : "gap-1.5"}`}>
          <div className="flex min-w-0 items-center gap-2.5">
            {!hideIcon && (
              <span
                className="inline-flex h-[1.75rem] w-[1.75rem] shrink-0 items-center justify-center rounded-md bg-gray-100 p-1 text-slate-400"
                aria-hidden
              >
                <IconComponent className="h-5 w-5 stroke-[1.5] text-gray-600" />
              </span>
            )}
            <div
              className={
                typeof title === "string"
                  ? "truncate text-xl font-bold leading-tight text-gray-900"
                  : "min-w-0 text-xl font-bold leading-tight text-gray-900"
              }
            >
              {title}
            </div>
          </div>
          {subTitle ? (
            <p className="m-0 text-sm text-slate-500">{subTitle}</p>
          ) : null}
          {shouldShowBreadcrumb ? (
            <nav aria-label="Breadcrumb">
              <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 text-sm leading-5">
                {breadcrumb.map((crumb, idx) => {
                  const isLast = idx === breadcrumb.length - 1;
                  return (
                    <React.Fragment key={`${crumb.title}-${idx}`}>
                      {idx > 0 ? (
                        <li
                          className="inline-flex items-center text-gray-400"
                          aria-hidden
                        >
                          <ChevronRight className="size-3.5 stroke-[2]" />
                        </li>
                      ) : null}
                      <li
                        className={
                          isLast
                            ? "font-semibold text-gray-900"
                            : "font-normal text-gray-500"
                        }
                        aria-current={isLast ? "page" : undefined}
                      >
                        {crumb.to && !isLast ? (
                          <Link
                            to={crumb.to}
                            className="font-normal text-gray-500 no-underline transition-colors hover:text-gray-700"
                          >
                            {crumb.title}
                          </Link>
                        ) : (
                          crumb.title
                        )}
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
            </nav>
          ) : null}
        </div>
        {centerContent && (
          <div className="flex-1 text-center">{centerContent}</div>
        )}
        <div className="flex shrink-0 items-center gap-3">
          {actions.map((action, idx) => (
            <div key={idx}>{action}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
